// Legal text templates (ES + EN). They consume the `legal` data from the admin
// (company name, CIF, address, etc.) so the user only needs to fill the form
// in /admin/content → Legal.

const buildBlock = (title, paragraphs) => ({ title, paragraphs });

export const buildLegalNotice = (legal, lang = 'es') => {
  const L = legal || {};
  const companyName = L.legalName || 'Euterpe Films, S.L.';
  const cif = L.cif || 'B-XXXXXXXX';
  const address = L.address || 'Dirección pendiente de configurar';
  const email = L.email || 'info@euterpefilms.com';
  const phone = L.phone || '';
  const registro = L.registroMercantil || 'Registro Mercantil pendiente';
  const website = L.websiteUrl || 'https://euterpefilms.com';
  const lastUpdate = L.lastUpdate || '';

  if (lang === 'en') {
    return {
      title: 'Legal Notice',
      lastUpdate,
      sections: [
        buildBlock('1. Owner of the website', [
          `In compliance with Article 10 of Law 34/2002 on Information Society Services and Electronic Commerce (LSSI), the following identifying data of the website ${website} is provided:`,
          `Company name: ${companyName}`,
          `Tax ID (CIF/NIF): ${cif}`,
          `Registered office: ${address}`,
          `Email: ${email}` + (phone ? ` · Phone: ${phone}` : ''),
          `Commercial Registry: ${registro}`,
        ]),
        buildBlock('2. Purpose', [
          `This website is intended to inform about the digital and physical cinema distribution services provided by ${companyName}, including DCP creation, KDM management, content delivery and physical poster logistics.`,
        ]),
        buildBlock('3. Conditions of use', [
          'Access to and use of this site grants the user the status of "User" and implies full acceptance of these terms.',
          'The User undertakes to use the website lawfully, in accordance with morality, public order and these legal terms.',
        ]),
        buildBlock('4. Intellectual and industrial property', [
          `All content on this website (texts, images, code, designs, logos, posters, trademarks) is the property of ${companyName} or its respective rights holders.`,
          'Reproduction, distribution or public communication without express written authorization is prohibited.',
        ]),
        buildBlock('5. Liability', [
          `${companyName} is not liable for the use that third parties may make of the information contained on this website, nor for damages of any kind that may arise from it.`,
          'The website may contain links to third-party sites for which we accept no responsibility.',
        ]),
        buildBlock('6. Applicable law and jurisdiction', [
          'This Legal Notice is governed by Spanish law. For any dispute arising from the use of the website, the parties submit to the Courts and Tribunals of the user’s domicile (when acting as a consumer) or, otherwise, of the city of Madrid (Spain).',
        ]),
      ],
    };
  }

  return {
    title: 'Aviso Legal',
    lastUpdate,
    sections: [
      buildBlock('1. Titular de la web', [
        `En cumplimiento del artículo 10 de la Ley 34/2002, de Servicios de la Sociedad de la Información y de Comercio Electrónico (LSSI-CE), se ponen a disposición de los usuarios los siguientes datos identificativos del sitio web ${website}:`,
        `Razón social: ${companyName}`,
        `CIF/NIF: ${cif}`,
        `Domicilio social: ${address}`,
        `Email de contacto: ${email}` + (phone ? ` · Teléfono: ${phone}` : ''),
        `Datos de inscripción: ${registro}`,
      ]),
      buildBlock('2. Objeto', [
        `El presente sitio web tiene por finalidad informar acerca de los servicios de distribución cinematográfica digital y física que presta ${companyName}, incluyendo la creación de DCP, la gestión de KDM, la entrega electrónica de contenidos y la logística de cartelería física.`,
      ]),
      buildBlock('3. Condiciones de uso', [
        'El acceso y uso de este sitio atribuye la condición de "Usuario" e implica la aceptación plena de las presentes condiciones.',
        'El Usuario se compromete a hacer un uso lícito del sitio, conforme a la moral, al orden público y al presente aviso legal.',
      ]),
      buildBlock('4. Propiedad intelectual e industrial', [
        `Todos los contenidos del sitio (textos, imágenes, código, diseños, logotipos, carteles, marcas) son propiedad de ${companyName} o de sus respectivos titulares de derechos.`,
        'Queda prohibida su reproducción, distribución o comunicación pública sin autorización expresa por escrito.',
      ]),
      buildBlock('5. Responsabilidad', [
        `${companyName} no se hace responsable del uso que terceros puedan hacer de la información contenida en esta web, ni de los daños o perjuicios de cualquier naturaleza que pudieran derivarse del mismo.`,
        'El sitio puede contener enlaces a webs de terceros sobre los que no asumimos ninguna responsabilidad.',
      ]),
      buildBlock('6. Legislación aplicable y jurisdicción', [
        'El presente Aviso Legal se rige por la legislación española. Para cualquier controversia derivada del uso del sitio, las partes se someten a los Juzgados y Tribunales del domicilio del usuario (cuando actúe como consumidor) o, en su defecto, de la ciudad de Madrid (España).',
      ]),
    ],
  };
};

export const buildPrivacyPolicy = (legal, lang = 'es') => {
  const L = legal || {};
  const companyName = L.legalName || 'Euterpe Films, S.L.';
  const address = L.address || 'Dirección pendiente de configurar';
  const dpoEmail = L.dpoEmail || L.email || 'dpo@euterpefilms.com';
  const email = L.email || dpoEmail;
  const lastUpdate = L.lastUpdate || '';

  if (lang === 'en') {
    return {
      title: 'Privacy Policy',
      lastUpdate,
      sections: [
        buildBlock('1. Data Controller', [
          `Identity: ${companyName}`,
          `Address: ${address}`,
          `Contact email: ${email}`,
          `Data Protection Officer: ${dpoEmail}`,
        ]),
        buildBlock('2. Purpose of data processing', [
          'We process the data you provide through our contact form to:',
          '• Reply to your inquiries and prepare commercial quotes for cinema distribution services.',
          '• Manage the contractual relationship if you become a customer.',
          '• Send commercial communications related to our services (only with your explicit consent).',
        ]),
        buildBlock('3. Legal basis', [
          'The legal basis is the consent of the interested party (Art. 6.1.a GDPR) for replying to the form and, where applicable, the execution of the contract (Art. 6.1.b GDPR) once the commercial relationship is established.',
        ]),
        buildBlock('4. Retention period', [
          'Data will be kept while there is a mutual interest or until the user requests deletion. Once the relationship has ended, data may be retained for the legally required period to attend to potential liabilities.',
        ]),
        buildBlock('5. Recipients', [
          'No data will be transferred to third parties unless legally required.',
          'We use cloud service providers (hosting, email) located in the European Union and bound by GDPR.',
        ]),
        buildBlock('6. User rights', [
          `You may exercise your rights of access, rectification, erasure, opposition, limitation and portability by writing to ${dpoEmail}, attaching a copy of an identity document.`,
          'You also have the right to file a complaint with the Spanish Data Protection Agency (www.aepd.es).',
        ]),
        buildBlock('7. Security measures', [
          'We have adopted the necessary technical and organizational measures to ensure the security of personal data and prevent its alteration, loss, unauthorized treatment or access.',
        ]),
      ],
    };
  }

  return {
    title: 'Política de Privacidad',
    lastUpdate,
    sections: [
      buildBlock('1. Responsable del tratamiento', [
        `Identidad: ${companyName}`,
        `Dirección: ${address}`,
        `Email de contacto: ${email}`,
        `Delegado de Protección de Datos: ${dpoEmail}`,
      ]),
      buildBlock('2. Finalidad del tratamiento', [
        'Tratamos los datos que nos facilites a través del formulario de contacto para:',
        '• Responder a tus consultas y elaborar presupuestos comerciales de servicios de distribución cinematográfica.',
        '• Gestionar la relación contractual si te conviertes en cliente.',
        '• Enviar comunicaciones comerciales relacionadas con nuestros servicios (solo con tu consentimiento expreso).',
      ]),
      buildBlock('3. Legitimación', [
        'La base legal es el consentimiento del interesado (Art. 6.1.a RGPD) para la respuesta al formulario y, en su caso, la ejecución del contrato (Art. 6.1.b RGPD) una vez establecida la relación comercial.',
      ]),
      buildBlock('4. Plazo de conservación', [
        'Los datos se conservarán mientras exista un interés mutuo o hasta que el usuario solicite su supresión. Finalizada la relación, los datos podrán conservarse por el plazo legalmente exigible para atender posibles responsabilidades.',
      ]),
      buildBlock('5. Destinatarios', [
        'No se cederán datos a terceros, salvo obligación legal.',
        'Utilizamos proveedores de servicios en la nube (hosting, email) ubicados en la Unión Europea y sujetos al RGPD.',
      ]),
      buildBlock('6. Derechos del usuario', [
        `Puedes ejercer tus derechos de acceso, rectificación, supresión, oposición, limitación y portabilidad escribiendo a ${dpoEmail} adjuntando copia de un documento identificativo.`,
        'Asimismo, tienes derecho a presentar una reclamación ante la Agencia Española de Protección de Datos (www.aepd.es).',
      ]),
      buildBlock('7. Medidas de seguridad', [
        'Hemos adoptado las medidas técnicas y organizativas necesarias para garantizar la seguridad de los datos personales y evitar su alteración, pérdida, tratamiento o acceso no autorizado.',
      ]),
    ],
  };
};

export const buildCookiePolicy = (legal, lang = 'es') => {
  const L = legal || {};
  const companyName = L.legalName || 'Euterpe Films, S.L.';
  const email = L.email || 'info@euterpefilms.com';
  const lastUpdate = L.lastUpdate || '';

  if (lang === 'en') {
    return {
      title: 'Cookie Policy',
      lastUpdate,
      sections: [
        buildBlock('1. What are cookies?', [
          'A cookie is a small data file that is downloaded to your device when you access certain websites. Cookies allow websites to store and retrieve information about your browsing behavior.',
        ]),
        buildBlock('2. Cookies used on this site', [
          `On this website, ${companyName} uses the following types of cookies:`,
          '• Strictly necessary cookies: required for the site to function correctly (session, navigation preferences, language). They do not require consent.',
          '• Preference cookies: remember the language you selected (ldc_lang).',
          '• Analytics cookies: anonymous statistics about navigation, only if you accept them in the cookie banner.',
        ]),
        buildBlock('3. Managing cookies', [
          'You can accept, reject or configure cookies through the banner displayed when you first enter the site.',
          'You can also delete cookies directly from your browser settings at any time.',
        ]),
        buildBlock('4. Updates', [
          `This policy may be updated based on regulatory changes. For any questions about cookies, please contact us at ${email}.`,
        ]),
      ],
    };
  }

  return {
    title: 'Política de Cookies',
    lastUpdate,
    sections: [
      buildBlock('1. ¿Qué son las cookies?', [
        'Una cookie es un pequeño fichero de datos que se descarga en tu dispositivo al acceder a determinadas páginas web. Las cookies permiten al sitio almacenar y recuperar información sobre tus hábitos de navegación.',
      ]),
      buildBlock('2. Cookies utilizadas en este sitio', [
        `En esta web, ${companyName} utiliza los siguientes tipos de cookies:`,
        '• Cookies estrictamente necesarias: imprescindibles para el correcto funcionamiento del sitio (sesión, navegación, preferencia de idioma). No requieren consentimiento.',
        '• Cookies de preferencia: almacenan el idioma que hayas seleccionado (ldc_lang).',
        '• Cookies analíticas: estadísticas anónimas sobre la navegación, únicamente si las aceptas en el banner de cookies.',
      ]),
      buildBlock('3. Gestión de las cookies', [
        'Puedes aceptar, rechazar o configurar las cookies a través del banner que aparece al entrar por primera vez en el sitio.',
        'También puedes eliminarlas directamente desde la configuración de tu navegador en cualquier momento.',
      ]),
      buildBlock('4. Actualizaciones', [
        `Esta política puede ser actualizada en función de cambios normativos. Para cualquier consulta sobre las cookies, escríbenos a ${email}.`,
      ]),
    ],
  };
};
